import React, { useState, useEffect, useMemo, useRef } from "react"
import { Modal, Radio, Input, Button, Flex, Form, Tooltip } from "antd"
import styles from "./Notify.module.css"
import { useTranslation } from "react-i18next"
import { validatePhoneNumber, PhoneInput } from "ui/Input/PhoneInput"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import PropTypes from "prop-types"

export const NotifyModal = ({ visible, onClose, onSend, loading, guestData, isRestrictedToAvailableMethods = false }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [sendMethod, setSendMethod] = useState("email")
  const [phoneValue, setPhoneValue] = useState("")
  // Pre-fill once per open. Re-running on every render would reset the guide's channel choice and
  // typed recipient — e.g. while the notify request is in flight and the parent re-renders.
  const isInitializedRef = useRef(false)

  const isPrimaryGuest = !!guestData?.is_primary_guest

  // Contact details we hold for this guest, ignoring which channel the waiver was shared by.
  // When the caller restricts the modal to available methods, a channel with no contact
  // detail on file can't be chosen.
  const availableEmail = guestData?.notify_details?.email || guestData?.shared_email || guestData?.guest_email || ""
  const availablePhone = guestData?.notify_details?.phone_number || guestData?.shared_phone || guestData?.phone_number || ""
  // Nothing on file for either channel — leave both open so the guide can still type a recipient.
  const hasAnyContact = !!availableEmail || !!availablePhone
  const isEmailDisabled = isRestrictedToAvailableMethods && hasAnyContact && !availableEmail
  const isTextDisabled = isRestrictedToAvailableMethods && hasAnyContact && !availablePhone

  // Resolve the email/phone/default method once from guestData so the same values
  // are reused on initial population and on every send-method switch.
  const { resolvedEmail, resolvedPhone, defaultMethod } = useMemo(() => {
    const notifyEmail = guestData?.notify_details?.email || ""
    const notifyPhone = guestData?.notify_details?.phone_number || ""

    if (guestData?.share_by === "email") {
      return {
        resolvedEmail: notifyEmail || guestData?.shared_email || "",
        resolvedPhone: "",
        defaultMethod: "email"
      }
    }

    if (guestData?.share_by === "text") {
      return {
        resolvedEmail: "",
        resolvedPhone: notifyPhone || guestData?.shared_phone || "",
        defaultMethod: "text"
      }
    }

    const email = isPrimaryGuest
      ? notifyEmail || guestData?.guest_email || guestData?.shared_email || ""
      : notifyEmail || guestData?.shared_email || ""
    const phone = isPrimaryGuest
      ? notifyPhone || guestData?.phone_number || guestData?.shared_phone || ""
      : notifyPhone || guestData?.shared_phone || ""

    return {
      resolvedEmail: email,
      resolvedPhone: phone,
      defaultMethod: phone && !email ? "text" : "email"
    }
  }, [guestData, isPrimaryGuest])

  useEffect(() => {
    if (!visible) {
      isInitializedRef.current = false
      return
    }
    if (isInitializedRef.current) return
    isInitializedRef.current = true

    // Never open on a channel the guest can't be reached on. When we override the default,
    // the shared-by resolution has no value for the other channel — fall back to what's on file.
    let method = defaultMethod
    let email = resolvedEmail
    let phone = resolvedPhone
    if (method === "email" && isEmailDisabled) {
      method = "text"
      phone = resolvedPhone || availablePhone
    } else if (method === "text" && isTextDisabled) {
      method = "email"
      email = resolvedEmail || availableEmail
    }

    setSendMethod(method)
    setPhoneValue(phone)
    form.setFieldsValue({
      sendMethod: method,
      email,
      phoneNumber: { fullNumber: phone, countryCode: "US" }
    })
  }, [visible, resolvedEmail, resolvedPhone, defaultMethod, isEmailDisabled, isTextDisabled, availableEmail, availablePhone, form])

  const handleSend = () => {
    form.validateFields().then((values) => {
      if (onSend) {
        onSend({ ...values, sendMethod })
      }
    })
  }

  const handleCancel = () => {
    form.resetFields()
    setSendMethod("email")
    if (onClose) {
      onClose()
    }
  }

  const handleSendMethodChange = (e) => {
    const newMethod = e.target.value
    setSendMethod(newMethod)
    // Restore the resolved values for the selected method so the form value stays
    // in sync with what the inputs display (prevents false "required" validation
    // and prevents the email going blank after switching away and back).
    setPhoneValue(resolvedPhone)
    form.setFieldsValue({
      email: resolvedEmail,
      phoneNumber: { fullNumber: resolvedPhone, countryCode: "US" }
    })
  }

  // A disabled Radio swallows mouse events, so the tooltip has to wrap it in a plain element
  // that still receives hover — only when disabled, so the enabled options behave as before.
  const renderMethodRadio = (value, label, isDisabled, disabledReason) => {
    const radio = (
      <Radio value={value} className={styles.radio} disabled={isDisabled}>
        {label}
      </Radio>
    )
    if (!isDisabled) return radio
    return (
      <Tooltip title={disabledReason}>
        <span className={styles.disabledRadioWrap}>{radio}</span>
      </Tooltip>
    )
  }

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={480}
      className={styles.shareWaiverModal}
      closeIcon={<span className={styles.closeIcon}>×</span>}
      centered
      maskClosable={false}
    >
      <Form form={form} layout="vertical">
        <div className="mb-xs">
          <h2 className={styles.modalTitle}>{t("digital_waiver_and_contracts.notify_guest")}</h2>
        </div>

        {/* Radio Buttons */}
        <div className="mb-xs">
          <Form.Item name="sendMethod" initialValue={sendMethod}>
            <Radio.Group onChange={handleSendMethodChange} className={styles.radioGroup}>
              {renderMethodRadio("text", t("embedded.booking_confirmation.send_by_text"), isTextDisabled, t("digital_waiver_and_contracts.no_phone_on_file"))}
              {renderMethodRadio("email", t("embedded.booking_confirmation.send_by_email"), isEmailDisabled, t("digital_waiver_and_contracts.no_email_on_file"))}
            </Radio.Group>
          </Form.Item>
        </div>

        {/* Phone Number Input */}
        {sendMethod === "text" && (
          <div className="mb-l">
            <Form.Item
              name="phoneNumber"
              label="Phone Number*"
              rules={
                isPrimaryGuest
                  ? []
                  : [
                      {
                        validator: (_, value) => {
                          if (!value?.fullNumber?.trim()) {
                            return Promise.reject("Please enter phone number")
                          }
                          return validatePhoneNumber(value.fullNumber)
                        }
                      }
                    ]
              }
            >
              <PhoneInput
                key={phoneValue}
                initialValue={phoneValue}
                // disabled={isPrimaryGuest}
                onChange={(value) => {
                  if (!isPrimaryGuest) form.setFieldValue("phoneNumber", value)
                }}
              />
            </Form.Item>
          </div>
        )}

        {/* Email Input */}
        {sendMethod === "email" && (
          <div className="mb-xs">
            <LabeledField
              name="email"
              labelKey="Email Address*"
              rules={
                isPrimaryGuest
                  ? []
                  : [
                      { required: true, message: "Please enter email address" },
                      { type: "email", message: "Please enter a valid email" }
                    ]
              }
            >
              <Input placeholder="Enter email address" className={styles.emailInput}
                // disabled={isPrimaryGuest}
              />
            </LabeledField>
          </div>
        )}

        {/* Footer */}
        <Flex justify="end" align="center">
          <Button type="primary" loading={loading} disabled={loading} onClick={handleSend}>
            {t("digital_waiver_and_contracts.notify")}
          </Button>
        </Flex>
      </Form>
    </Modal>
  )
}

NotifyModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onSend: PropTypes.func,
  loading: PropTypes.bool,
  // Opt-in: disable the channel the guest has no contact detail for (used by the new booking detail page).
  isRestrictedToAvailableMethods: PropTypes.bool,
  guestData: PropTypes.shape({
    is_primary_guest: PropTypes.bool,
    share_by: PropTypes.string,
    shared_email: PropTypes.string,
    shared_phone: PropTypes.string,
    guest_email: PropTypes.string,
    phone_number: PropTypes.string,
    notify_details: PropTypes.shape({
      email: PropTypes.string,
      phone_number: PropTypes.string
    })
  })
}
